/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/* eslint-disable react/no-unknown-property */
import React, {Fragment, useEffect, useState, useRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {FormattedMessage, useIntl} from 'react-intl'

import loadable from '@loadable/component'
const PageNotFound = loadable(() => import('./../page-not-found'))

// Components
import {Box, Button, Flex, Slide, Stack, Text} from '@chakra-ui/react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {
    useUsid,
    useShopperContext,
    useProduct,
    useShopperCustomersMutation,
    useCustomerId,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper,
} from '@salesforce/commerce-sdk-react'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useGetPdpPlus} from '../../custom-api/hooks/use-get-pdp-plus'
import RecommendedProducts from '../../components/recommended-products'
import {AccordionBlock, PromoSliderVideo, SideCard} from '../../components/editorial-components'
import ProductDocumentation from './partials/product-documentation'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// constant
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
} from '@salesforce/retail-react-app/app/constants'

import {
    MAX_CACHE_AGE_PDP,
    STALE_WHILE_REVALIDATE_PDP,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
} from '../../../app/constants'

import {
    rebuildPathWithParams,
    removeSiteLocaleFromPath,
} from '@salesforce/retail-react-app/app/utils/url'

import Awards from './partials/awards'
import Bundle from '../../components/bundle'
import KeyFeatures from '../../components/editorial-components/key-features'
import OfficinaPage from '../officina'
import ProductCompare from '../../components/product-compare'
import ProductView from '../../components/product-view'
import StickyMenu from './partials/StickyMenu'
import YoutubeIframe from '../../components/youtube-video'
import {BRAND_LABEL, BRAND_SHORT_LABEL} from '../../brand'
import {Redirect} from 'react-router-dom/cjs/react-router-dom'
import {formatCards} from './formatKeyFeatures'
import {getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import {getMetaTags, parseAndSanitizeJSONWithSpaces, isUntypedTrue} from '../../utils/utils'
import {remCalc} from '../../theme/_utils'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useToast} from '../../hooks/use-toast'
import AlternateMetaTag from '../../components/metatag/alternate'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMutationObserver from '../../custom-hooks/use-mutation-observer'
import useSpecifications from '../../utils/specifications-utils'
import {
    addProductToBreadcrumbs,
    getPriceData,
    isProductPurchasable,
    shouldShowPageNotFound,
} from '../../utils/product-utils'
import {productUrlBuilder} from '../../utils/url'
import SanitizedHtml from '../../components/html-sanitizer'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'

import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useWishList} from '../../hooks/use-wish-list'
import {MID} from '../../custom-api/hooks/use-marketing-cloud-tracking'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import WhatsInTheBox from '../../components/whats-in-the-box'
import Features from '../../components/features'
import {useVariant} from '../../hooks/use-variant'
import usePdpBannerRecovery from './hooks/use-pdp-banner-recovery'
import {useWazzup} from '../../hooks/use-wazzup'

const ProductDetail = () => {
    const {formatMessage} = useIntl()

    const history = useHistory()
    const location = useLocation()
    const activeData = useActiveData()
    const toast = useToast()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {addLayer} = useGoogleTagManager()
    const {currency} = useCurrency()
    const {site, locale} = useMultiSite()

    const productViewRef = useRef(null)
    const productDetailRef = useRef(null)
    const awardsRef = useRef(null)
    const benefitsRef = useRef(null)
    const keyFeaturesRef = useRef(null)
    const specificationsRef = useRef(null)
    const similarRef = useRef(null)
    const accessoriesRef = useRef(null)
    const isLocationChangeEventSent = useRef(null)

    const isBraun = BRAND_SHORT_LABEL === 'BR'

    const {customParams} = getConfig()
    const {buildLocalizedUrl} = useMulticountryUtils()

    /****************************** Basket *********************************/
    const {data: basket, isLoading: isBasketLoading, refetch: refetchBasket} = useCurrentBasket()
    const createBasket = useShopperBasketsMutation('createBasket')
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_PDP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_PDP}`,
        )
    }

    /*************************** Product Detail and Category ********************/
    const {urlSegment} = useParams()
    const safeDecode = (value) =>
        typeof value === 'string' ? decodeURIComponent(value)?.replaceAll('+', ' ') : null
    const productId = safeDecode(urlSegment.match(/([A-Za-z0-9.%+-]+)\.html/)?.[1])

    const urlParams = new URLSearchParams(location.search)
    const isProductQueryEnabled = !!urlParams.get('pid') || !!productId

    const {
        data: product,
        isFetching: isProductFetching,
        isFetched: isProductFetched,
        isError: isProductError,
        refetch: refetchProduct,
    } = useProduct(
        {
            parameters: {
                id: urlParams.get('pid') || productId,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
                locale: locale.id,
                currency,
            },
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true,
            enabled: isProductQueryEnabled,
        },
    )

    const [showStickyMenu, setShowStickyMenu] = useState(false)

    // Show sticky menu when the product view component is out of view
    useEffect(() => {
        const handleScroll = () => {
            if (productViewRef.current?.node) {
                const productViewBottom = productViewRef.current.node.getBoundingClientRect().bottom
                setShowStickyMenu(productViewBottom <= 0)
            }
        }

        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    /**************** Personalization *************************/
    const [hasInjectedContent, setHasInjectedContent] = useState(false)
    const [hasFbtInjectedContent, setHasFbtInjectedContent] = useState(false)

    const recommendationsContainerRef = useRef(null)
    const fbtRecommendationsContainerRef = useRef(null)

    useMutationObserver(
        recommendationsContainerRef,
        (mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.target.childNodes.length > 0) {
                    setHasInjectedContent(true)
                    return
                }
            }
            setHasInjectedContent(false)
        },
        {
            childList: true,
        },
    )

    useMutationObserver(
        fbtRecommendationsContainerRef,
        (mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.target.childNodes.length > 0) {
                    setHasFbtInjectedContent(true)
                    return
                }
            }
            setHasFbtInjectedContent(false)
        },
        {
            childList: true,
        },
    )

    const c_breadcrumbs = useMemo(() => {
        if (product?.c_breadcrumbs) return addProductToBreadcrumbs(product)
    }, [product?.c_breadcrumbs])

    /**************** Specifications Accordion ****************/
    //TODO: export all the messages to a formatted messages for value_name
    const accordionData = useSpecifications(product)

    const isOfficina = product?.c_isOfficina === 'true'

    /****************************** Sets and Bundles *********************************/
    const isProductABundle = product?.c_isBundle === 'true'
    const variant = useVariant(product)

    const gtmFiredRef = useRef(false)

    useEffect(() => {
        return () => {
            gtmFiredRef.current = false
        }
    }, [location.pathname])

    /**************** Product Variant ****************/
    useEffect(() => {
        if (!variant) {
            return
        }
        // update the variation attributes parameter on
        // the url accordingly as the variant changes
        const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            pid: variant?.productId,
        })
        if (urlParams.get('pid') !== variant?.productId) {
            history.replace(updatedUrl)
        }
    }, [variant])

    /**************** Corporate ****************/
    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    usePdpBannerRecovery({
        productId,
        pid: urlParams.get('pid'),
        isProductQueryEnabled,
        isProductFetched,
        isProductFetching,
        hasBannerInfo: !!product?.c_bannerInfo,
        basketId: basket?.basketId,
        isBasketLoading,
        shopperContext,
        createBasket,
        refetchBasket,
        refetchProduct,
        pathname: location.pathname,
        search: location.search,
    })

    const wazzupConfig = useWazzup()

    /**************** PdpPlus Components ****************/
    const {data: pdpPlusData} = useGetPdpPlus(productId, {enabled: !!productId})

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )

    const handleAddToWishlist = (product, variant, quantity) => {
        const isItemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id,
        )

        if (!isItemInWishlist) {
            createCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                    },
                    body: {
                        // NOTE: API does not respect quantity, it always adds 1
                        quantity,
                        productId: variant?.productId || product?.id,
                        public: true,
                        priority: 1,
                        type: 'product',
                    },
                },
                {
                    onSuccess: () => {
                        toast({
                            title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                            status: 'success',
                        })
                    },
                    onError: () => {
                        showError()
                    },
                },
            )
        } else {
            toast({
                title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                status: 'info',
                action: (
                    <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                        {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                    </Button>
                ),
            })
        }
    }

    /**************** Add To Cart ****************/
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const handleAddToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues.map(({variant, quantity, product}) => ({
                productId: variant.productId,
                price: variant.price,
                quantity,
                c_MaterialGroup: product.c_materialGroup,
                c_categoryName: product?.c_categoryName,
                c_subModelIdentifier: product?.c_subModelIdentifier,
                c_prdhaCategory: product?.c_prdhaCategory,
            }))
            await addItemToNewOrExistingBasket(productItems)

            // einstein.sendAddToCart(productItems)

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            console.error('error', error)
            showError(error)
        }
    }

    /**************** GTM Event ****************/
    // This event is fired only for SKUs
    useEffect(() => {
        if (!gtmFiredRef.current) {
            if (product && productId !== product?.id) {
                window.dispatchEvent(new Event('productDetailLoaded'))
                addLayer(polish_ecommerce_gtm_event.bind(this))
                addLayer(
                    item_gtm_event.bind(this, {
                        event: 'view_item',
                        currency: currency,
                        items: [
                            {
                                item_name: product?.c_defaultName,
                                item_id: product?.id,
                                item_category: product?.c_defaultCategoryName,
                                item_variant: product?.c_subModelIdentifier,
                                price: +(variant?.price / (1 + product?.c_taxRate / 100)).toFixed(
                                    2,
                                ),
                                fullprice: getPriceData(product)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '',
                                // get_reevoo_rating() || get_bv_rating(),
                                erp_category: product?.c_materialGroup,
                                prdha_category: product?.c_prdha_category,
                                stock_status:
                                    product?.inventory?.stockLevel > 0
                                        ? 'in-stock'
                                        : 'out-of-stock',
                            },
                        ],
                    }),
                )
                gtmFiredRef.current = true

                if (isLocationChangeEventSent.current !== location.pathname) {
                    setTimeout(() => {
                        //console.log('[SFCC] Location change PDP')
                        window.dispatchEvent(new Event('locationchange'))
                    }, 150)
                    isLocationChangeEventSent.current = location.pathname
                }

                if (window._etmc) {
                    window._etmc.push(['setOrgId', MID])
                    window._etmc.push(['trackPageView', {item: product?.id}])
                }
            }
        }
    }, [product])

    /**************** Einstein ****************/
    useEffect(() => {
        if (product && product.type.set) {
            // einstein.sendViewProduct(product)
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    // einstein.sendViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child},
                    })
                }
                activeData.sendViewProduct({id: product?.primaryCategoryId}, child, 'detail')
            })
        } else if (product) {
            try {
                // einstein.sendViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product},
                })
            }
            activeData.sendViewProduct({id: product?.primaryCategoryId}, product, 'detail')
        }
    }, [product])

    let featureCards
    /********KEY FEATURES********/
    if (product?.[isBraun ? 'c_benefit' : 'c_keyFeature']) {
        let parsedFeatures = parseAndSanitizeJSONWithSpaces(
            product?.[isBraun ? 'c_benefit' : 'c_keyFeature'],
            product?.c_subModelIdentifier,
        )
        featureCards = formatCards(parsedFeatures)
    }

    /**
     * SEO Redirects
     */

    const pathnameNoLocale = removeSiteLocaleFromPath(location.pathname)

    const redirectFunctionality = (redirectUrl) => {
        try {
            if (res) {
                res.status(301).location(redirectUrl)
                return
            } else {
                return (
                    <Redirect
                        to={{
                            pathname: redirectUrl,
                        }}
                    />
                )
            }
        } catch (e) {
            console.error(e)
        }
    }

    if (
        product?.c_variationGroup_slugUrl &&
        decodeURIComponent(product?.c_variationGroup_slugUrl) !==
            decodeURIComponent(pathnameNoLocale) &&
        product?.c_variationGroup_slugUrl !== pathnameNoLocale
    ) {
        // SKU
        const redirectUrl = `/${locale.alias}${product?.c_variationGroup_slugUrl}${location.search}`
        return redirectFunctionality(redirectUrl)
    } else if (
        !product?.c_variationGroup_slugUrl &&
        product?.c_slugUrl &&
        decodeURIComponent(product?.c_slugUrl) !== decodeURIComponent(pathnameNoLocale) &&
        product?.c_slugUrl !== pathnameNoLocale
    ) {
        // Variation
        const redirectUrl = `/${locale.alias}${product?.c_slugUrl}${location.search}`
        return redirectFunctionality(redirectUrl)
    }

    /**************** Error Handling ****************/

    const show404 = shouldShowPageNotFound({
        isProductError,
        isProductQueryEnabled,
        isProductFetching,
        product,
    })

    if (show404) {
        if (res) res.status(404)
        return <PageNotFound />
    }

    if (isOfficina) return <OfficinaPage product={product} />

    /**************** SideCard ****************/
    const generateDescriptionWithBullets = (product) => {
        const bulletPoints = product?.c_summary

        return `${product?.longDescription || ''}${
            bulletPoints ? `<div style="margin-top: 22px;">${bulletPoints}</div>` : ''
        }`
    }

    const baseUrl = getDamBaseUrl()

    let urlCanonical = buildLocalizedUrl(`${productUrlBuilder(product)}`)
    const pid = urlParams.get('pid')
    if (pid) urlCanonical += `?pid=${pid}`
    else if (variant) urlCanonical += `?pid=${variant?.productId}`

    const recommendations = (product?.c_recommendations || []).filter(
        (rec) => rec?.recommendationType?.value === 2,
    )

    const specificationsTitle = formatMessage({
        id: 'product_detail.specifications.title',
        defaultMessage: 'Specifications',
    })

    const stripHtmlTags = (str) => (str ? str.replace(/<[^>]*>/g, '') : '')
    const cleanDescription = stripHtmlTags(product?.longDescription || product?.shortDescription)

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: product?.name,
        description: cleanDescription,
        url: `https://${customParams.currentHost}${urlCanonical}`,
    }

    const schemaImageLink = findImageGroupBy(product?.imageGroups, {
        viewType: 'medium',
        selectedVariationAttributes: product?.variationValues,
    })?.images?.[0]?.link

    const availability =
        (product?.c_skuIsPDPOnly === true || product?.c_skuIsPDPOnly === 'true') &&
        !isProductPurchasable(product)
            ? 'https://schema.org/Discontinued'
            : !product?.inventory?.orderable || product?.inventory?.orderable === 'false'
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock'

    const productSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: product?.name,
        image: {
            '@type': 'ImageObject',
            contentUrl: schemaImageLink,
            creditText: `De'Longhi Appliances S.r.l`,
        },
        brand: {'@type': 'Brand', name: BRAND_LABEL},
        offers: {
            '@type': 'Offer',
            url: `https://${customParams.currentHost}${urlCanonical}`,
            priceCurrency: currency,
            price: variant?.price,
            availability: availability,
        },
    }

    const categories = product?.c_breadcrumbs || []

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: categories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }
    const createImageGroupForPdp = (partialPath) => {
        if (!partialPath) {
            return null
        }

        const renditions = {
            desktop: '807x590',
            mobile: '343x260',
            tablet_1024: '807x590',
            tablet_768: '343x260',
        }
        const pathParts = partialPath.split('/assets/')
        const assetId = pathParts.length > 1 ? pathParts[1] : null
        if (!assetId) {
            console.error('Could not extract asset ID from path:', partialPath)
            return null
        }
        // --- Build the imageGroup object ---
        const imageGroup = {
            // The key must be "image_pdp" to match the 'type' prop.
            image_pdp: {
                desktop: {
                    rendition: renditions.desktop,
                    dam_image: [{id: assetId}],
                },
                mobile: {
                    rendition: renditions.mobile,
                    dam_image: [{id: assetId}],
                },
                tablet_1024: {
                    rendition: renditions.tablet_1024,
                    dam_image: [{id: assetId}],
                },
                tablet_768: {
                    rendition: renditions.tablet_768,
                    dam_image: [{id: assetId}],
                },
            },
        }

        return imageGroup
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        contentUrl: schemaImageLink || '',
        creditText: `De'Longhi Appliances S.r.l`,
    }

    // Parse "What's in the box" JSON attribute
    let whatsInTheBoxItems = []
    if (product?.c_whatsInTheBox) {
        try {
            whatsInTheBoxItems = JSON.parse(product.c_whatsInTheBox)
        } catch (e) {
            whatsInTheBoxItems = []
        }
    }
    const handleFocusLinkClick = (dataAbValue) => {
        const selector = `[data-ab="${dataAbValue}"]`
        const targetElement = document.querySelector(selector)

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        } else {
            console.warn(`[ProductDetail] Scroll target not found for selector: ${selector}`)
        }
    }

    return (
        <Box
            style={{position: 'relative'}}
            className="sf-product-detail-page"
            data-testid="product-details-page"
            data-gtm="product-details-page"
            ref={productDetailRef}
            overflowX="hidden"
        >
            <Helmet>
                {getMetaTags(product?.c_pageMetaTagRules)}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(productSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={`https://${customParams.currentHost}${urlCanonical}`} />
            </Helmet>
            {product?.c_hreflangs && (
                <AlternateMetaTag links={product.c_hreflangs} pid={urlParams.get('pid')} />
            )}
            <Slide
                direction="top"
                data-testid="sticky-menu-slider"
                in={showStickyMenu}
                aria-hidden={!showStickyMenu}
                style={{
                    width: '100vw',
                    zIndex: 10,
                    top: 0,
                }}
            >
                <StickyMenu
                    product={product}
                    awardsRef={awardsRef}
                    benefitsRef={benefitsRef}
                    keyFeaturesRef={keyFeaturesRef}
                    specificationsRef={specificationsRef}
                    similarRef={similarRef}
                    accessoriesRef={accessoriesRef}
                    renderActionButtons={productViewRef.current?.renderActionButtons}
                    isHidden={!showStickyMenu}
                />
            </Slide>
            {!isUntypedTrue(shopperContext?.customQualifiers?.isCorporate) &&
                pdpPlusData?.top_editorial_components?.map((component) => (
                    <EditorialComponentsFactory
                        key={component.uid}
                        componentType={component?._content_type_uid}
                        {...component}
                        data-ab={`editorial-top-${component?._content_type_uid}`}
                    />
                ))}
            <Stack gap={0}>
                <Fragment>
                    <Box layerStyle="page">
                        <ProductView
                            // Do not use an arrow function as we are manipulating the functions scope.
                            ref={function (node) {
                                // we need the ref to the node to determine if the product view is in view
                                // and we also get the renderActionButtons function to use it in the sticky menu
                                // it is made available here with the `this` keyword because it is bound to this scope from inside the ProductView component
                                productViewRef.current = {
                                    node,
                                    renderActionButtons: this.renderActionButtons,
                                }
                            }}
                            product={product}
                            category={c_breadcrumbs || []}
                            addToCart={(variant, quantity) =>
                                handleAddToCart([{product, variant, quantity}])
                            }
                            isCorporate={isUntypedTrue(
                                shopperContext?.customQualifiers?.isCorporate,
                            )}
                            pdpPlusData={pdpPlusData}
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={!isProductFetched}
                            isBasketLoading={isBasketLoading}
                            isCategoryLoading={!isProductFetched}
                            isWishlistLoading={isWishlistLoading}
                            isBraun={isBraun}
                            onFocusLinkClick={handleFocusLinkClick}
                        />
                    </Box>
                    {(product?.c_sidecardImage || generateDescriptionWithBullets(product)) && (
                        <SideCard
                            imageGroup={createImageGroupForPdp(product?.c_sidecardImage)}
                            imageUrl={
                                product?.c_sidecardImage
                                    ? `${baseUrl}/${product.c_sidecardImage}`
                                    : null
                            }
                            imageAlt={''}
                            textGroupPdp={{
                                bodyCopy: generateDescriptionWithBullets(product),
                                expandCtaTitle: formatMessage({
                                    defaultMessage: 'Read more',
                                    id: 'sidecard.read_more',
                                }),
                            }}
                            type={'pdp'}
                            imagePosition="right"
                            hasPaddings={true}
                            backgroundColor="white"
                            button={null}
                        />
                    )}

                    <Flex flexDir={isBraun ? 'column-reverse' : 'column'} data-ab="tech-spec">
                        <Box ref={specificationsRef}>
                            <AccordionBlock title={specificationsTitle} items={accordionData} />
                        </Box>
                    </Flex>

                    <Flex flexDir={isBraun ? 'column-reverse' : 'column'}>
                        <Box>
                            {isProductABundle && (
                                <>
                                    <Flex
                                        background="background.light"
                                        justifyContent="center"
                                        data-ab="bundle-fbt"
                                    >
                                        {/* Checks whether the product is part of a bundle and whether the ids of the items forming the bundle are correct */}
                                        {product?.c_bundleProductsInfo?.filter(
                                            (child) => child.productId,
                                        )?.length > 1 ? (
                                            <Bundle
                                                title={
                                                    <FormattedMessage
                                                        defaultMessage="The Perfect Match"
                                                        id="bundle_card.title"
                                                    />
                                                }
                                                currentPageProduct={product}
                                            />
                                        ) : (
                                            <></>
                                        )}
                                    </Flex>
                                </>
                            )}
                            <Flex
                                background="background.light"
                                justifyContent="center"
                                id="pdp_product_recommendation_fbt"
                                ref={fbtRecommendationsContainerRef}
                            />
                            {!hasFbtInjectedContent && (
                                <Flex background="background.light" justifyContent="center">
                                    <RecommendedProducts
                                        products={recommendations.map(
                                            (rec) => rec.recommendedItemId,
                                        )}
                                        title={
                                            <FormattedMessage
                                                defaultMessage="Frequently Bought Together"
                                                id="recommended_product_card_variant.title"
                                            />
                                        }
                                        variant={'greyBackgroundVariant'}
                                        tileImageVariant={'transparent'}
                                        prefix="frequently-bought-together"
                                        gtmCarouselName="carousel_pdp"
                                    />
                                </Flex>
                            )}
                        </Box>
                        {/* Render What's in the box only for KW and BR brand.
                            TODO: check if this attribute is filled only on BR/KW products
                            (hence the specific check for brand is not needed) */}
                        {['BR', 'KW'].includes(BRAND_SHORT_LABEL) &&
                            whatsInTheBoxItems.length > 0 && (
                                <Flex justifyContent="center">
                                    <WhatsInTheBox
                                        items={whatsInTheBoxItems}
                                        variant={!isBraun ? 'greyBackgroundVariant' : ''}
                                    />
                                </Flex>
                            )}
                    </Flex>

                    {product?.c_youtubeVideoGeneric &&
                        (() => {
                            const selectedVideo = parseAndSanitizeJSONWithSpaces(
                                product?.c_youtubeVideoGeneric,
                                product?.c_subModelIdentifier,
                            )?.[1]

                            if (!selectedVideo || !selectedVideo.videolink) return null

                            return (
                                <Box
                                    style={{
                                        width: '100%',
                                        maxWidth: remCalc(1440),
                                        margin: `${remCalc(40)} auto`,
                                        aspectRatio: 2 / 1,
                                    }}
                                    data-ab="editorial-video-generic"
                                >
                                    <YoutubeIframe
                                        videoSrc={selectedVideo.videolink}
                                        title={selectedVideo.title}
                                    />
                                </Box>
                            )
                        })()}
                    {product?.c_award && (
                        <Box ref={awardsRef} data-ab="editorial-awards">
                            <Awards awards={product.c_award} p={product?.c_subModelIdentifier} />
                        </Box>
                    )}
                    {product?.c_benefit && !isBraun && (
                        <Box ref={benefitsRef} data-ab={'editorial-benefit'}>
                            {parseAndSanitizeJSONWithSpaces(
                                product.c_benefit,
                                product?.c_subModelIdentifier,
                            )?.map((benefit, index) => {
                                return (
                                    <SideCard
                                        key={index}
                                        imageGroup={createImageGroupForPdp(benefit?.imageLink)}
                                        imageUrl={`${baseUrl}/${benefit?.imageLink}`}
                                        imageAlt={''}
                                        textGroupPdp={{
                                            title: benefit?.title,
                                            bodyCopy: benefit?.description,
                                        }}
                                        type={'pdp'}
                                        imagePosition={index % 2 !== 0 ? 'right' : 'left'}
                                        hasPaddings={true}
                                        backgroundColor="white"
                                        button={null}
                                        mobileFullSizePdp={true}
                                        titleAs="h2"
                                    />
                                )
                            })}
                        </Box>
                    )}

                    {product?.[isBraun ? 'c_benefit' : 'c_keyFeature'] && (
                        <Box>
                            <Box ref={keyFeaturesRef} data-ab="editorial-key-features">
                                <KeyFeatures
                                    title={formatMessage({
                                        id: 'product_detail.key_features.title',
                                        defaultMessage: 'Key Features',
                                    })}
                                    cards={featureCards}
                                    isSlider
                                    aspectRatio="4/3"
                                />
                            </Box>
                        </Box>
                    )}

                    {product?.c_keyFeature && isBraun && (
                        <Features features={product?.c_keyFeature} />
                    )}

                    <Flex flexDir={isBraun ? 'column-reverse' : 'column'}>
                        <Box
                            layerStyle="page"
                            display={{md: 'flex'}}
                            justifyContent={{md: 'center'}}
                        >
                            <ProductDocumentation product={product} />
                        </Box>
                    </Flex>

                    {product?.c_youtubeVideoHowTo && (
                        <PromoSliderVideo
                            abTitle="editorial-howto"
                            sliderGroup={parseAndSanitizeJSONWithSpaces(
                                product?.c_youtubeVideoHowTo,
                                product?.c_subModelIdentifier,
                            )?.map((video) => ({
                                text_block: {
                                    title: video.title,
                                },
                                video_link: {
                                    href: video.videolink,
                                },
                                thumbnail: video.medialink ? {src: video.medialink} : {},
                            }))}
                            contentType="howto"
                        />
                    )}
                    <Box layerStyle="page" data-ab="compare">
                        <ProductCompare product={product} />
                    </Box>
                    <Box ref={similarRef}>
                        <Flex
                            justifyContent="center"
                            id="pdp_product_recommendations_ymal"
                            ref={recommendationsContainerRef}
                        />
                        {!hasInjectedContent && (
                            <Flex
                                justifyContent="center"
                                id="pdp_product_recommendations_ymal_cust"
                            >
                                <RecommendedProducts
                                    personalizationId="pdp_product_recommendations_ymal"
                                    products={(product?.c_recommendations || [])
                                        .filter((rec) => rec?.recommendationType?.value === 1)
                                        .map((rec) => rec.recommendedItemId)}
                                    tileImageVariant={'transparent'}
                                    title={
                                        <FormattedMessage
                                            defaultMessage="You may also like"
                                            id="may_also_like_product_card.title"
                                        />
                                    }
                                    prefix="ymal"
                                    gtmCarouselName="carousel_pdp"
                                />
                            </Flex>
                        )}
                    </Box>
                    {[product?.c_disclaimerText, product?.c_disclaimerText2]
                        .filter(Boolean)
                        .map((text, idx) => (
                            <Box
                                key={idx}
                                layerStyle="page"
                                paddingY={{
                                    base: remCalc(8),
                                    md: remCalc(16),
                                    lg: remCalc(24),
                                    xl: remCalc(40),
                                }}
                                data-ab="disclaimer"
                            >
                                <SanitizedHtml
                                    as={Text}
                                    html={text}
                                    textStyle="body-medium"
                                    color="text.default"
                                    sanitize={true}
                                />
                            </Box>
                        ))}
                </Fragment>
            </Stack>
            <div
                data-ab="reviews"
                data-bv-show="reviews"
                data-bv-product-id={
                    product?.c_subModelIdentifier
                        ? product.c_subModelIdentifier
                              .replace(/\s+/g, '_')
                              .replace(/:/g, '_')
                              .replace(/\+/g, '_')
                              .replace(/\//g, '_')
                        : undefined
                }
                style={{margin: 'auto', maxWidth: remCalc(1400)}}
            ></div>
            {wazzupConfig && product && (
                <>
                    <div
                        className="w-conf"
                        data-code={wazzupConfig.code}
                        data-friend={wazzupConfig.friend}
                    ></div>
                    <div
                        className="w-product"
                        code={variant?.productId || product.id}
                        name={product.name}
                        price={
                            variant?.price ||
                            getPriceData(product)?.salePrice ||
                            getPriceData(product)?.regularPrice ||
                            ''
                        }
                        url={window.location.href}
                        image={schemaImageLink || ''}
                    />
                </>
            )}
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object,
}

export default ProductDetail
